import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { baseUrl } from '../constant/config';
import useRecordingPublish, { requestErrorMessage } from './useRecordingPublish';

const PLATFORMS = [
  { key: 'facebook', label: 'Facebook Page', icon: FacebookIcon, color: '#1877F2' },
  { key: 'instagram', label: 'Instagram Reel', icon: InstagramIcon, color: '#E1306C' },
];

// Instagram rejects captions over 2200 characters.
const CAPTION_LIMIT = 2200;

export default function ShareRecordingDialog({ open, onClose, video }) {
  const [selected, setSelected] = useState([]);
  const [caption, setCaption] = useState('');
  const { publish: send, publishing, checking, results, notice, reset, busy } = useRecordingPublish(video);

  // Which platforms the server can actually publish to.
  const {
    data: targets,
    isLoading: targetsLoading,
    isError: targetsFailed,
    error: targetsError,
    refetch: retryTargets,
    isFetching: targetsFetching,
  } = useQuery(
    ['shareTargets'],
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/share-targets`);
      return data.data;
    },
    // One quick retry, then show the problem with a Retry button rather than
    // a spinner that never ends.
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: open, retry: 1 }
  );

  useEffect(() => {
    if (open) {
      setSelected([]);
      reset();
      setCaption(video?.debate?.title ? `${video.debate.title}` : '');
    }
  }, [open, video, reset]);

  const publish = () => send(selected, caption);

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));

  const anyConfigured = targets && PLATFORMS.some((p) => targets[p.key]?.configured);
  const alreadyPublished = (key) =>
    video?.shares?.some((s) => s.platform === key && s.status === 'published');

  return (
    <Dialog open={open} onClose={publishing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share recording</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {video?.debate?.title || 'Untitled debate'}
        </Typography>

        {targetsLoading && <CircularProgress size={20} />}

        {targetsFailed && !targets && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => retryTargets()} disabled={targetsFetching}>
                {targetsFetching ? 'Retrying…' : 'Retry'}
              </Button>
            }
          >
            Could not load the sharing options. {requestErrorMessage(targetsError)}
          </Alert>
        )}

        {targets && !anyConfigured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No platform is connected yet. Add the Facebook and Instagram credentials to the backend
            <code style={{ margin: '0 4px' }}>.env</code> file, then restart the API.
          </Alert>
        )}

        {targets && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {PLATFORMS.map(({ key, label, icon: Icon, color }) => {
              const target = targets[key] || {};
              const disabled = !target.configured || busy;
              const row = (
                <FormControlLabel
                  key={key}
                  disabled={disabled}
                  control={
                    <Checkbox checked={selected.includes(key)} onChange={() => toggle(key)} disabled={disabled} />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon sx={{ color: disabled ? 'text.disabled' : color }} />
                      <span>{label}</span>
                      {alreadyPublished(key) && <Chip size="small" label="already posted" />}
                    </Stack>
                  }
                />
              );

              return target.configured ? (
                row
              ) : (
                <Tooltip key={key} title={`Server is missing: ${(target.missing || []).join(', ')}`} placement="right">
                  <span>{row}</span>
                </Tooltip>
              );
            })}
          </Stack>
        )}

        <TextField
          label="Caption"
          multiline
          minRows={3}
          fullWidth
          value={caption}
          disabled={busy}
          onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_LIMIT))}
          helperText={`${caption.length} / ${CAPTION_LIMIT}`}
        />

        {notice && (
          <Alert
            severity={checking ? 'warning' : 'info'}
            icon={checking ? <CircularProgress size={18} /> : undefined}
            sx={{ mt: 2 }}
          >
            {notice}
          </Alert>
        )}

        {publishing && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
            Publishing. Instagram transcodes the video first, so this can take up to two minutes -
            keep this window open.
          </Alert>
        )}

        {results && (
          <Box sx={{ mt: 2 }}>
            {results.map((r) => (
              <Alert
                key={r.platform}
                severity={r.status === 'published' ? 'success' : 'error'}
                sx={{ mb: 1 }}
              >
                <strong style={{ textTransform: 'capitalize' }}>{r.platform}</strong>
                {r.status === 'published' ? (
                  <>
                    {' published.'}
                    {r.permalink && (
                      <>
                        {' '}
                        <Link href={r.permalink} target="_blank" rel="noreferrer">
                          View post
                        </Link>
                      </>
                    )}
                  </>
                ) : (
                  <>: {r.error}</>
                )}
              </Alert>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publishing}>
          {results ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={() => publish()}
          disabled={busy || selected.length === 0}
          startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {publishing ? 'Publishing...' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ShareRecordingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  video: PropTypes.object,
};
